<template>
    <form class="font-form" @submit.prevent="submit">
        <hox-input
            v-if="internalFormData.name !== undefined"
            data-testid="add-font-modal__name-input"
            :model-value="internalFormData.name"
            is-required
            @input="onFontNameChange"
        >
            <template #label>Font name</template>
        </hox-input>
        <hox-tabs color="tertiary" size="small" data-testid="add-font-modal__tabs">
            <hox-tabs-item
                v-for="tab of tabs"
                :key="tab.value"
                :is-active="tab.value === activeTabValue"
                :data-testid="tab.testId"
                @click="setActiveTabValue(tab.value)"
            >
                {{ tab.label }}
            </hox-tabs-item>
        </hox-tabs>
        <div class="font-form__tabbed-content">
            <div v-if="activeTabValue === 'upload'">
                <font-upload
                    v-if="uploadConfig"
                    :config="uploadConfig"
                    @fontPreviewUpdate="onFontPreviewUpdate"
                    @fontUploaded="onFontUploaded"
                    @fontUploadFailed="onFontUploadFailed"
                />
            </div>
            <div v-if="activeTabValue === 'external'">
                <hox-input
                    ref="fontUrl"
                    data-testid="add-font-modal__url-input"
                    :model-value="internalFormData.url"
                    @input="onFontUrlChange"
                >
                    <template #label>Font url</template>
                </hox-input>
            </div>
            <div v-if="showFontPreview" class="font-form__preview">
                <label class="font-form__preview-label">Font Preview</label>
                <div class="font-form__preview-text">
                    <hox-loading-layer v-if="isLoadingFont" :is-full-screen="false" size="small" />
                    <span v-else>{{ previewText }}</span>
                </div>
            </div>
        </div>
    </form>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import FontUpload from "@/components/FontUpload.vue";
import type { Err } from "@/types/types";
import { debounce } from "@/utils";

export type FontFormData = {
    key?: string;
    name?: string;
    url?: string;
    sectionId?: string;
};

export default defineComponent({
    name: "FontForm",

    components: {
        FontUpload
    },

    props: {
        uploadConfig: {
            required: true,
            type: Object
        },

        initialFormData: {
            type: Object as () => FontFormData
        },

        fontPreviewUrl: {
            type: String
        }
    },

    emits: ["validationErrorsUpdate", "formDataUpdate", "formTabSwitched", "submit"],

    data() {
        const tabs = [
            { label: "Upload font", value: "upload", testId: "upload-font__tab" },
            { label: "External font", value: "external", testId: "external-font__tab" }
        ];

        return {
            tabs,
            activeTabValue: tabs[0].value,
            isLoadingFont: false,
            previewText: "The Quick Brown Fox Jumps Over The Lazy Dog",
            showFontPreview: false,
            internalFormData: {
                ...this.initialFormData,
                sectionId: this.initialFormData?.sectionId || "none"
            }
        };
    },

    computed: {
        keyValue() {
            return (this.internalFormData.key || "").trim();
        },

        nameValue() {
            return (this.internalFormData.name || "").trim();
        },

        urlValue() {
            return (this.internalFormData.url || "").trim();
        },

        validationErrors() {
            const errors = [];

            if ((!this.keyValue && !this.urlValue) || !this.showFontPreview) {
                errors.push("A valid font to upload was not found");
            }

            if (!this.nameValue) {
                errors.push("A font name is required");
            }

            return errors;
        }
    },

    watch: {
        validationErrors: {
            deep: true,
            immediate: true,
            handler() {
                this.$emit("validationErrorsUpdate", [...this.validationErrors]);
            }
        },

        internalFormData: {
            deep: true,
            immediate: true,
            handler() {
                this.$emit("formDataUpdate", { ...this.internalFormData });
            }
        }
    },

    async mounted() {
        if (this.initialFormData?.url) {
            this.activeTabValue = "external";
        }
        if (this.fontPreviewUrl) {
            await this.setFontPreview(this.fontPreviewUrl);
        }
    },

    methods: {
        onFontPreviewUpdate(fontUrl: string) {
            this.setFontPreview(fontUrl);
        },

        onFontUploaded(fontUrl: string) {
            this.internalFormData.key = fontUrl;
            this.internalFormData.url = "";
            this.setName(fontUrl);
        },

        onFontUploadFailed() {
            this.$snackbar.error(
                "Font Error",
                "There was an unexpected error uploading the font. Please try again in a few moments."
            );
        },

        onFontNameChange(fontName: string) {
            const name = fontName.trim();
            this.internalFormData.name = name;
        },

        onFontUrlChange: debounce(function handler(fontUrl: string) {
            const url = fontUrl.trim();
            this.setFontPreview(url);
            this.internalFormData.url = url;
            this.internalFormData.key = "";
            this.setName(url);
        }, 500),

        async setFontPreview(fontUrl: string) {
            if (!fontUrl) {
                this.showFontPreview = false;
                return;
            }

            try {
                this.isLoadingFont = true;
                this.showFontPreview = true;
                const previewFont = new FontFace("CustomFontPreview", `url("${fontUrl}")`);
                const fontFace = await previewFont.load();
                document.fonts.add(fontFace);
            } catch (err) {
                this.showFontPreview = false;
                this.$snackbar.error("Font Not Found", (err as Err).message);
            } finally {
                this.isLoadingFont = false;
            }
        },

        async setActiveTabValue(tabValue: string) {
            this.activeTabValue = tabValue;
            // Only empty if not in edit mode
            if (!this.initialFormData) {
                this.showFontPreview = false;
                this.internalFormData.key = "";
                this.internalFormData.url = "";
                // We set this to undefined to hide the field when we switch tabs
                this.internalFormData.name = undefined;
            }
            // Emit switch event
            this.$emit("formTabSwitched");
            // Focus on input if we've switched to the external tab
            await this.$nextTick();
            if (this.$refs.fontUrl) {
                this.$refs.fontUrl.focus();
            }
        },

        setName(fontUrl: string) {
            if (!this.nameValue) {
                const matches = /.*\/(.+?)\./.exec(fontUrl);
                const name = matches && matches.length > 1 ? matches[1] : "";

                this.internalFormData.name = name;
            }
        },

        submit() {
            this.$emit("submit");
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.font-form {
    &__preview {
        &-label {
            display: inline-block;
            margin: 0 0 8px;
        }

        &-text {
            display: block;
            border: 1px solid $cmp-light-border-color;
            font-family: "CustomFontPreview";
            font-size: 32px;
            min-height: 100px;
            padding: $spacing-small;
            position: relative;
            margin: 0 0 $spacing-small;
            text-align: center;
        }
    }

    &__tabbed-content {
        padding: $spacing 0;
    }
}
</style>
